/**
 * The problem report's form, in the drawer (bata-platform#686, ADR 9063; the structured report of
 * bata-platform ADR 2137/1020, ADR 9071).
 *
 * Four kinds — a question for a person to answer («Clarification»), a figure, list or cell to
 * check («Data»), something that is wrong («Malfunction») and something the product does not do
 * («Enhancement request») — each asking for what the team needs to act on it without a second
 * round: the requirement, an example, the screen, the market and the run, the figures expected and
 * how we will know it is done ({@link FIELDS_OF_KIND}). The context the team needs to reproduce it
 * is attached without asking the person for it: the path they were on, the app version, their
 * language and the correlation ids of their last requests. There are no screenshots: a planning
 * screen carries the client's commercial figures, and the correlation id leads to the cause where a
 * screenshot shows only the symptom.
 *
 * **The rubric is the platform's.** `rubric.ts` is a copy that marks the missing fields before
 * «Send»; the platform answers 422 on what it finds incomplete, and that is the authority.
 *
 * **The context is taken when the form opens, not when it is sent.** The person keeps working
 * while they type (the drawer is not modal) and the screen keeps reading: the last five calls
 * would otherwise be five background refreshes by the time they press «Send», and the one that
 * failed would be gone. A snapshot also keeps the body stable across a retry, so the transport's
 * idempotency key — held per decision — makes a second press after a dropped connection the same
 * report rather than a second one.
 *
 * **Pre-filled from OPTIMA.** «Open in the form» under a draft brings every field, the kind and the
 * conversation it came from; the person edits and sends it here, and the platform links the two.
 *
 * The section is pre-filled from the route and editable. «New area» is offered only for an
 * enhancement: a malfunction is always about something that exists. If the kind is switched away
 * from enhancement while «New area» is chosen, the section goes back to the screen the person is on.
 */

import { Send } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { recentCalls, useProblemMessage } from '../../api';
import { APP_VERSION } from '../../lib/build-info';
import { Button, Checkbox, Combobox, FieldShell, FOCUS_RING, Input, RadioGroup, useNotify, type SelectOption } from '../../primitives';

import { useShellAccess } from './access';
import { FEEDBACK_LIMITS, REPORT_KINDS, STRUCTURE_LIMITS, useSubmitFeedback, type FeedbackKind, type FeedbackRequest } from './api';
import { ListField } from './ListField';
import { FIELDS_OF_KIND, isRequired, missingFields, type RubricField } from './rubric';
import type { ScreenScope } from './screen-scope';
import {
  GENERAL_SECTION, NEW_AREA_SECTION, SECTION_ID_PATTERN, feedbackSections, sectionForPath, type SectionTarget,
} from './sections';
import { KIND_LABEL_KEY } from './words';

export interface FeedbackPrefill {
  readonly kind?: FeedbackKind;
  readonly title?: string;
  readonly description?: string;
  readonly examples?: string;
  /** Section ids; the first is the report's section. */
  readonly sectionIds?: readonly string[];
  readonly markets?: readonly string[];
  readonly runs?: readonly string[];
  readonly expectedFigures?: string;
  readonly expectedFiguresNotApplicableReason?: string;
  readonly acceptanceCriteria?: readonly string[];
  readonly steps?: string;
  /** The OPTIMA conversation the draft came from. */
  readonly assistantConversationId?: string;
}

export interface FeedbackFormProps {
  /** The path the person is on. */
  readonly route: string;
  /** The market and the run in the address of the screen the person is on. */
  readonly scope?: ScreenScope;
  readonly prefill?: FeedbackPrefill;
  /** Called once the report is sent and acknowledged. */
  readonly onSent: () => void;
}

/** The look of the app's other free-text areas (`ComposeScreen`'s lists), in the body face. */
const TEXTAREA = [
  'border-hairline bg-surface-raised text-ink-primary text-body font-ui w-full rounded-control border px-3 py-2',
  'aria-[invalid=true]:border-status-critical',
  FOCUS_RING,
].join(' ');

/** The words of the requirement field, per kind: what is wrong, what is needed, which figure, the question. */
const REQUIREMENT_KEYS: Readonly<Record<FeedbackKind, { readonly label: string; readonly help: string }>> = {
  clarification: { label: 'feedback.requirement.clarification.label', help: 'feedback.requirement.clarification.help' },
  data: { label: 'feedback.requirement.data.label', help: 'feedback.requirement.data.help' },
  incident: { label: 'feedback.description.label', help: 'feedback.description.help' },
  enhancement: { label: 'feedback.requirement.enhancement.label', help: 'feedback.requirement.enhancement.help' },
};

const KIND_HELP_KEY: Readonly<Record<FeedbackKind, string>> = {
  clarification: 'feedback.kind.help.clarification',
  data: 'feedback.kind.help.data',
  incident: 'feedback.kind.help.malfunction',
  enhancement: 'feedback.kind.help.enhancement',
};

const ERROR_KEY: Readonly<Record<RubricField, string>> = {
  title: 'feedback.error.title',
  requirement: 'feedback.error.description',
  examples: 'feedback.error.examples',
  screens: 'feedback.error.screens',
  markets: 'feedback.error.markets',
  runs: 'feedback.error.runs',
  expected_figures: 'feedback.error.expectedFigures',
  acceptance_criteria: 'feedback.error.acceptanceCriteria',
  steps: 'feedback.error.title',
};

const isKind = (value: string): value is FeedbackKind => (REPORT_KINDS as readonly string[]).includes(value);
const cut = (text: string | undefined, max: number): string => (text ?? '').slice(0, max);
const list = (items: readonly string[] | undefined, max: number): readonly string[] => (items ?? []).filter((item) => item.trim() !== '').slice(0, max);

export function FeedbackForm({ route, scope, prefill, onSent }: FeedbackFormProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const notify = useNotify();
  const send = useSubmitFeedback();
  const problemText = useProblemMessage();
  const access = useShellAccess();

  // Taken once, when the form opens (see the header).
  const [context] = useState(() => ({
    route,
    // Only ids of the shape the platform accepts, and at most its five: one it would refuse must
    // not cost the person their report.
    correlationIds: recentCalls().map((call) => call.correlationId)
      .filter((id) => FEEDBACK_LIMITS.correlationIds.pattern.test(id))
      .slice(-FEEDBACK_LIMITS.correlationIds.max),
    scope: scope ?? { markets: [], runs: [] },
  }));
  const sections = useMemo(() => feedbackSections(access), [access]);
  const here = useMemo(() => sectionForPath(context.route, sections), [context.route, sections]);
  const prefilledSections = list(prefill?.sectionIds, STRUCTURE_LIMITS.sections.maxItems).filter((id) => SECTION_ID_PATTERN.test(id));

  const [kind, setKind] = useState<FeedbackKind>(prefill?.kind ?? 'incident');
  const [sectionId, setSectionId] = useState(prefilledSections[0] ?? here);
  const [otherSections, setOtherSections] = useState<readonly string[]>(prefilledSections.slice(1));
  const [addingKey, setAddingKey] = useState(0);
  const [title, setTitle] = useState(cut(prefill?.title, FEEDBACK_LIMITS.title.max));
  // The crash screen's message can be longer than the platform accepts; the textarea's maxLength
  // does not cut a value set in code, so the pre-fill is cut here.
  const [description, setDescription] = useState(cut(prefill?.description, FEEDBACK_LIMITS.description.max));
  const [examples, setExamples] = useState(cut(prefill?.examples, STRUCTURE_LIMITS.examples.max));
  const [markets, setMarkets] = useState<readonly string[]>(
    prefill?.markets === undefined ? context.scope.markets.slice(0, STRUCTURE_LIMITS.markets.maxItems) : list(prefill.markets, STRUCTURE_LIMITS.markets.maxItems),
  );
  const [runs, setRuns] = useState<readonly string[]>(list(prefill?.runs, STRUCTURE_LIMITS.runs.maxItems));
  const [expectedFigures, setExpectedFigures] = useState(cut(prefill?.expectedFigures, STRUCTURE_LIMITS.expectedFigures.max));
  const [notApplicable, setNotApplicable] = useState((prefill?.expectedFiguresNotApplicableReason ?? '').trim() !== '');
  const [notApplicableReason, setNotApplicableReason] = useState(cut(prefill?.expectedFiguresNotApplicableReason, STRUCTURE_LIMITS.expectedFigures.max));
  const [criteria, setCriteria] = useState<readonly string[]>(list(prefill?.acceptanceCriteria, STRUCTURE_LIMITS.acceptanceCriteria.maxItems));
  const [steps, setSteps] = useState(cut(prefill?.steps, FEEDBACK_LIMITS.steps.max));
  const [attempted, setAttempted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const fields = FIELDS_OF_KIND[kind];
  const sectionLabel = (section: SectionTarget): string => {
    const screen = section.parentLabelKey === undefined
      ? t(section.labelKey)
      : t('nav.subScreen', { screen: t(section.parentLabelKey), view: t(section.labelKey) });
    return section.chapterLabelKey === section.labelKey
      ? screen
      : t('feedback.section.option', { chapter: t(section.chapterLabelKey), screen });
  };
  const labelOf = (id: string): string => {
    if (id === GENERAL_SECTION) return t('feedback.section.general');
    if (id === NEW_AREA_SECTION) return t('feedback.section.newArea');
    const section = sections.find((candidate) => candidate.id === id);
    return section === undefined ? id : sectionLabel(section);
  };
  const options: readonly SelectOption[] = [
    { value: GENERAL_SECTION, label: t('feedback.section.general') },
    ...(kind === 'enhancement' ? [{ value: NEW_AREA_SECTION, label: t('feedback.section.newArea') }] : []),
    ...sections.map((section) => ({ value: section.id, label: sectionLabel(section) })),
    // A section a draft named that this build does not list is kept, as itself.
    ...(sectionId !== GENERAL_SECTION && sectionId !== NEW_AREA_SECTION && !sections.some((s) => s.id === sectionId)
      ? [{ value: sectionId, label: sectionId }] : []),
  ];
  const addOptions: readonly SelectOption[] = sections
    .filter((section) => section.id !== sectionId && !otherSections.includes(section.id))
    .map((section) => ({ value: section.id, label: sectionLabel(section) }));

  const changeKind = (next: string): void => {
    if (!isKind(next)) return;
    setKind(next);
    if (next !== 'enhancement' && sectionId === NEW_AREA_SECTION) setSectionId(here);
  };

  const screens = [sectionId, ...otherSections];
  const missing = new Set(missingFields({
    kind, title, requirement: description, examples, screens, markets, runs, expectedFigures,
    expectedFiguresNotApplicableReason: notApplicable ? notApplicableReason : '', acceptanceCriteria: criteria,
  }));
  const sectionInvalid = !SECTION_ID_PATTERN.test(sectionId);
  const invalid = sectionInvalid || missing.size > 0;
  const errorOf = (field: RubricField): { errorKey: string } | Record<string, never> => (
    attempted && missing.has(field) ? { errorKey: ERROR_KEY[field] } : {}
  );
  const required = (field: RubricField): boolean => isRequired(kind, field);

  const body = (): FeedbackRequest => {
    const asks = (field: RubricField): boolean => fields.has(field);
    const text = (value: string): string | null => (value.trim() === '' ? null : value.trim());
    return {
      kind,
      section_id: sectionId,
      title: title.trim(),
      description: description.trim(),
      steps: asks('steps') ? text(steps) : null,
      route: context.route.slice(0, FEEDBACK_LIMITS.route.max),
      app_version: APP_VERSION.slice(0, FEEDBACK_LIMITS.appVersion.max),
      locale: i18n.language.slice(0, FEEDBACK_LIMITS.locale.max),
      correlation_ids: context.correlationIds,
      affected_sections: screens.filter((id) => id !== GENERAL_SECTION),
      ...(asks('examples') ? { examples: text(examples) } : {}),
      ...(asks('markets') ? { markets } : {}),
      ...(asks('runs') ? { affected_runs: runs } : {}),
      ...(asks('expected_figures') ? {
        expected_figures: notApplicable && kind === 'enhancement' ? null : text(expectedFigures),
        ...(kind === 'enhancement' && notApplicable ? { expected_figures_not_applicable_reason: text(notApplicableReason) } : {}),
      } : {}),
      ...(asks('acceptance_criteria') ? { acceptance_criteria: criteria } : {}),
      ...(prefill?.assistantConversationId === undefined ? {} : { assistant_conversation_id: prefill.assistantConversationId }),
    };
  };

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setAttempted(true);
    if (invalid || busy) return;
    setBusy(true);
    setFailure(null);
    try {
      const receipt = await send(body());
      notify({ titleKey: 'feedback.sent.title', titleValues: { code: receipt.code }, descriptionKey: 'feedback.sent.body' });
      onSent();
    } catch (error) {
      setFailure(error);
      setBusy(false);
    }
  };

  const textArea = (
    field: RubricField, labelKey: string, helpKey: string, value: string, set: (next: string) => void, max: number, rows: number,
  ): React.JSX.Element => (
    <FieldShell labelKey={labelKey} helpKey={helpKey} required={required(field)} {...errorOf(field)}>
      {({ controlId, describedBy, invalid: isInvalid }) => (
        <textarea
          id={controlId}
          aria-describedby={describedBy}
          aria-invalid={isInvalid || undefined}
          value={value}
          onChange={(event) => set(event.target.value)}
          maxLength={max}
          rows={rows}
          className={TEXTAREA}
        />
      )}
    </FieldShell>
  );

  const onScreenRun = context.scope.runs.find((run) => !runs.includes(run));

  return (
    <form onSubmit={(event) => { void submit(event); }} noValidate className="flex flex-col gap-4" data-feedback-form="">
      {prefill?.assistantConversationId !== undefined && (
        <p className="text-caption text-ink-secondary" data-feedback-from-optima="">{t('feedback.fromOptima')}</p>
      )}
      <div className="flex flex-col gap-1">
        <RadioGroup
          labelKey="feedback.kind.label"
          options={REPORT_KINDS.map((value) => ({ value, labelKey: KIND_LABEL_KEY[value] }))}
          value={kind}
          onValueChange={changeKind}
        />
        <p className="text-caption text-ink-secondary">{t(KIND_HELP_KEY[kind])}</p>
      </div>

      {/* Remounted when the kind changes: the combobox keeps what was typed in it, and a value
          set from outside (the section going back to the screen) would not show. */}
      <Combobox
        key={kind}
        labelKey="feedback.section.label"
        helpKey="feedback.section.help"
        options={options}
        value={sectionId}
        onValueChange={setSectionId}
        {...(attempted && sectionInvalid ? { errorKey: 'feedback.error.section' } : errorOf('screens'))}
      />
      {fields.has('screens') && (
        <div className="flex flex-col gap-2">
          {otherSections.length > 0 && (
            <ul aria-label={t('feedback.sections.others')} className="flex flex-wrap gap-1">
              {otherSections.map((id) => (
                <li key={id} className="bg-pill text-caption text-ink-primary rounded-chip inline-flex items-center gap-1 px-2 py-1">
                  <span>{labelOf(id)}</span>
                  <Button variant="ghost" size="sm" onClick={() => setOtherSections(otherSections.filter((other) => other !== id))}>
                    {t('feedback.list.removeNamed', { item: labelOf(id) })}
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {screens.length < STRUCTURE_LIMITS.sections.maxItems && (
            <Combobox
              key={`add-${addingKey}`}
              labelKey="feedback.sections.add"
              options={addOptions}
              value=""
              onValueChange={(next) => {
                if (next === '' || otherSections.includes(next) || next === sectionId) return;
                setOtherSections([...otherSections, next]);
                setAddingKey((was) => was + 1);
              }}
            />
          )}
        </div>
      )}

      <Input
        labelKey="feedback.title.label"
        maxLength={FEEDBACK_LIMITS.title.max}
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        required
        {...errorOf('title')}
      />

      {textArea('requirement', REQUIREMENT_KEYS[kind].label, REQUIREMENT_KEYS[kind].help, description, setDescription, FEEDBACK_LIMITS.description.max, 5)}

      {fields.has('examples') && textArea('examples', 'feedback.examples.label', 'feedback.examples.help', examples, setExamples, STRUCTURE_LIMITS.examples.max, 3)}

      {fields.has('markets') && (
        <ListField
          labelKey="feedback.markets.label"
          helpKey="feedback.markets.help"
          required={required('markets')}
          {...errorOf('markets')}
          items={markets}
          onChange={setMarkets}
          maxItems={STRUCTURE_LIMITS.markets.maxItems}
          upperCase
        />
      )}

      {fields.has('runs') && (
        <ListField
          labelKey="feedback.runs.label"
          helpKey="feedback.runs.help"
          required={required('runs')}
          {...errorOf('runs')}
          items={runs}
          onChange={setRuns}
          maxItems={STRUCTURE_LIMITS.runs.maxItems}
          maxLength={STRUCTURE_LIMITS.runs.max}
          extra={onScreenRun === undefined || runs.length >= STRUCTURE_LIMITS.runs.maxItems ? undefined : (
            <Button variant="ghost" size="sm" onClick={() => setRuns([...runs, onScreenRun])}>{t('feedback.runs.attach')}</Button>
          )}
        />
      )}

      {fields.has('expected_figures') && (
        <div className="flex flex-col gap-2">
          {!(kind === 'enhancement' && notApplicable)
            && textArea('expected_figures', 'feedback.expectedFigures.label', 'feedback.expectedFigures.help', expectedFigures, setExpectedFigures, STRUCTURE_LIMITS.expectedFigures.max, 3)}
          {kind === 'enhancement' && (
            <>
              <Checkbox labelKey="feedback.expectedFigures.notApplicable" checked={notApplicable} onCheckedChange={setNotApplicable} />
              {notApplicable && (
                <Input
                  labelKey="feedback.expectedFigures.reason"
                  maxLength={STRUCTURE_LIMITS.expectedFigures.max}
                  value={notApplicableReason}
                  onChange={(event) => setNotApplicableReason(event.target.value)}
                  required
                  {...errorOf('expected_figures')}
                />
              )}
            </>
          )}
        </div>
      )}

      {fields.has('acceptance_criteria') && (
        <ListField
          labelKey="feedback.criteria.label"
          helpKey="feedback.criteria.help"
          required={required('acceptance_criteria')}
          {...errorOf('acceptance_criteria')}
          items={criteria}
          onChange={setCriteria}
          maxItems={STRUCTURE_LIMITS.acceptanceCriteria.maxItems}
          maxLength={STRUCTURE_LIMITS.acceptanceCriteria.max}
        />
      )}

      {fields.has('steps') && textArea('steps', 'feedback.steps.label', 'feedback.steps.help', steps, setSteps, FEEDBACK_LIMITS.steps.max, 4)}

      <p className="text-caption text-ink-secondary">{t('feedback.context.note')}</p>

      {attempted && invalid && (
        <p role="status" className="text-caption text-status-critical" data-feedback-missing="">
          {t('feedback.missing.summary', { count: missing.size + (sectionInvalid ? 1 : 0) })}
        </p>
      )}

      {failure !== null && (
        <div role="alert" className="flex flex-col gap-1">
          <p className="text-body text-status-critical font-semibold">{t('feedback.error.failed')}</p>
          <p className="text-caption text-ink-secondary">{problemText(failure)}</p>
        </div>
      )}

      <div className="flex flex-col items-end gap-2">
        <Button type="submit" variant="primary" icon={<Send size={14} aria-hidden />} loading={busy} disabled={busy}>
          {t('feedback.submit')}
        </Button>
        <p className="text-caption text-ink-secondary text-end">{t('feedback.ai.notice')}</p>
      </div>
    </form>
  );
}
